import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { getTimezoneOption, type TimezoneOption } from 'utils/util/timezone';
import ModalEditor from 'views/Widgets/Modals/ModalEditor';
import { SCHEDULE_INPUTS, type ScheduledParticipant } from './consts';

interface Props {
  isOpen: boolean;
  toggle: () => void;
  participant: ScheduledParticipant | null;
  onSave: (participant: ScheduledParticipant) => void;
}

interface EditParticipantFormData {
  scheduled_time?: string | null;
  scheduled_timezone?: string | null;
}

// Retimes one already-queued person. No people picker: who the row is for is
// fixed, and changing that is "remove this one, add that one" - a different
// row, with its own history.
const EditScheduledParticipantModal: FC<Props> = ({
  isOpen,
  toggle,
  participant,
  onSave,
}) => {
  const { formatMessage } = useIntl();

  const [formObj, setFormObj] = useState<EditParticipantFormData>({});

  useEffect(() => {
    if (!isOpen || !participant) {
      return;
    }
    setFormObj({
      scheduled_time: participant.scheduled_time,
      scheduled_timezone: getTimezoneOption(
        participant.scheduled_timezone
      ) as unknown as string,
    });
  }, [isOpen, participant]);

  const inputs = useMemo(
    () =>
      SCHEDULE_INPUTS(formatMessage, {
        dateLabel: formatMessage({
          id: 'app.views.administration.custom_surveys.surveys.edit_participant.scheduled_time_label',
          defaultMessage: 'When should they receive it?',
        }),
        // No minDate, unlike the add flow: an overdue row the scheduler
        // hasn't picked up yet still has to open in the picker rather than
        // be rejected for the date it already holds.
      }),
    [formatMessage]
  );

  const handleChange = useCallback((newObj: EditParticipantFormData) => {
    setFormObj(newObj);
  }, []);

  const transformObjectBeforeSubmit = useCallback(
    (data: EditParticipantFormData): ScheduledParticipant | null => {
      if (!participant) {
        return null;
      }
      return {
        ...participant,
        scheduled_time: data.scheduled_time as string,
        scheduled_timezone:
          (data.scheduled_timezone as unknown as TimezoneOption)?.value ?? '',
      };
    },
    [participant]
  );

  return (
    <ModalEditor
      isOpen={isOpen}
      toggle={toggle}
      title={formatMessage(
        {
          id: 'app.views.administration.custom_surveys.surveys.edit_participant.title',
          defaultMessage: 'Change send time for {name}',
        },
        { name: participant?.person?.full_name ?? '' }
      )}
      object={formObj}
      inputs={inputs}
      onChange={handleChange}
      transformObjectBeforeSubmit={transformObjectBeforeSubmit}
      submitText={formatMessage({
        id: 'app.views.administration.custom_surveys.surveys.edit_participant.submit',
        defaultMessage: 'Save',
      })}
      disableUnsavedChangesPrompt={true}
      triggerCallbackBeforeClose={true}
      callback={onSave}
    />
  );
};

export default React.memo(EditScheduledParticipantModal);
